import { type MouseEvent, type SyntheticEvent, useMemo, useState } from "react";

import i18next, { t } from "i18next";

import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  SxProps,
  TextField,
  Theme
} from "@mui/material";

import {
  NumberRangeFilterValue,
  NumberRangePresetSet,
  formatNumberRangeLabel,
  getNumberPresetsForSet,
  isNumberRangeEmpty,
  isNumberRangeFilterValue
} from "@/src/components/organisms/AdminDataTable/numberRangeFilter";
import { Column } from "@/src/components/organisms/AdminDataTable/reactTable";

const triggerSx: SxProps<Theme> = (theme) => ({
  width: "100%",
  justifyContent: "space-between",
  textTransform: "none",
  backgroundColor: theme.palette.background.paper,
  borderColor: theme.palette.misc.outlineBorder,
  color: theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightRegular,
  px: 1,
  minHeight: 40,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary
  }
});

const popoverContentSx: SxProps<Theme> = {
  p: 2,
  width: 300,
  display: "flex",
  flexDirection: "column",
  gap: 1.5
};

const presetRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 0.75
};

interface NumberRangeFilterInputProps<TData extends object> {
  column: Column<TData, unknown>;
  presetSet?: NumberRangePresetSet;
}

const readInitialValue = (raw: unknown): NumberRangeFilterValue | undefined => {
  if (!isNumberRangeFilterValue(raw)) return undefined;
  if (isNumberRangeEmpty(raw)) return undefined;
  return raw;
};

const toNumberOrUndefined = (s: string): number | undefined => {
  if (s.trim() === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

const NumberRangeFilterInput = <TData extends object>(
  props: NumberRangeFilterInputProps<TData>
) => {
  const { column, presetSet = "price" } = props;
  const presets = useMemo(() => getNumberPresetsForSet(presetSet), [presetSet]);

  const initial = readInitialValue(column.getFilterValue());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftMin, setDraftMin] = useState<string>(String(initial?.min ?? ""));
  const [draftMax, setDraftMax] = useState<string>(String(initial?.max ?? ""));
  const [draftPresetId, setDraftPresetId] = useState<string | undefined>(
    initial?.presetId
  );

  const isOpen = Boolean(anchorEl);
  const triggerLabel = formatNumberRangeLabel(initial, presets);
  const hasActiveRange = !isNumberRangeEmpty(initial);

  const openPopover = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setDraftMin(String(initial?.min ?? ""));
    setDraftMax(String(initial?.max ?? ""));
    setDraftPresetId(initial?.presetId);
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => setAnchorEl(null);

  const handlePresetClick = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    setDraftMin(String(preset.min ?? ""));
    setDraftMax(String(preset.max ?? ""));
    setDraftPresetId(presetId);
  };

  const handleMinChange = (value: string) => {
    setDraftMin(value);
    setDraftPresetId(undefined);
  };

  const handleMaxChange = (value: string) => {
    setDraftMax(value);
    setDraftPresetId(undefined);
  };

  const handleApply = () => {
    const min = toNumberOrUndefined(draftMin);
    const max = toNumberOrUndefined(draftMax);
    if (min === undefined && max === undefined) {
      column.setFilterValue(undefined);
    } else {
      const next: NumberRangeFilterValue = {
        min,
        max,
        presetId: draftPresetId
      };
      column.setFilterValue(next);
    }
    closePopover();
  };

  const handleClear = () => {
    setDraftMin("");
    setDraftMax("");
    setDraftPresetId(undefined);
    column.setFilterValue(undefined);
    closePopover();
  };

  const stopPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };

  return (
    <Box onClick={stopPropagation} onMouseDown={stopPropagation}>
      <Button
        variant="outlined"
        size="small"
        color={hasActiveRange ? "primary" : "inherit"}
        onClick={openPopover}
        startIcon={<FilterListIcon fontSize="small" />}
        sx={triggerSx}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            textAlign: "left",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {triggerLabel}
        </Box>
      </Button>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { onClick: stopPropagation } }}
      >
        <Box sx={popoverContentSx}>
          <Box sx={presetRowSx}>
            {presets.map((preset) => (
              <Chip
                key={preset.id}
                label={preset.label}
                size="small"
                color={draftPresetId === preset.id ? "primary" : "default"}
                variant={draftPresetId === preset.id ? "filled" : "outlined"}
                onClick={() => handlePresetClick(preset.id)}
              />
            ))}
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              type="number"
              size="small"
              fullWidth
              label={t("admin.dataTable.filters.numberMin")}
              value={draftMin}
              onChange={(e) => handleMinChange(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              type="number"
              size="small"
              fullWidth
              label={t("admin.dataTable.filters.numberMax")}
              value={draftMax}
              onChange={(e) => handleMaxChange(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ justifyContent: "flex-end" }}
          >
            <Button size="small" onClick={handleClear}>
              {t("admin.dataTable.filters.clear")}
            </Button>
            <Button size="small" variant="contained" onClick={handleApply}>
              {t("admin.dataTable.filters.apply")}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default NumberRangeFilterInput;
